import { 
  Lead, 
  NurtureSequence, 
  ActivityEvent, 
  EngagementMetrics, 
  ConversionFunnel, 
  PastClient,
  DashboardMetrics,
  Channel,
  SequenceType,
  PipelineStage
} from '@/types';

// Utility to generate random data
const randomBetween = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const randomChoice = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const firstNames = [
  'Sarah', 'Mike', 'Jennifer', 'David', 'Amanda', 'Robert', 'Lisa', 'Chris', 'Michelle', 'James',
  'Jessica', 'Brian', 'Ashley', 'Kevin', 'Nicole', 'Ryan', 'Rachel', 'Mark', 'Stephanie', 'Daniel',
  'Megan', 'Andrew', 'Lauren', 'Matthew', 'Brittany', 'Jason', 'Andrea', 'Josh', 'Samantha', 'Tyler',
  'Emily', 'Adam', 'Amy', 'John', 'Maria', 'Tom', 'Anna', 'Nick', 'Laura', 'Steve'
];

const lastNames = [
  'Johnson', 'Smith', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
  'Hernandez', 'Lopez', 'Gonzales', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
  'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson',
  'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores'
];

// const cities = [
//   'Los Angeles', 'San Diego', 'Costa Mesa', 'Irvine', 'Huntington Beach', 'Newport Beach', 'Anaheim',
//   'Orange', 'Tustin', 'Santa Ana', 'Fullerton', 'Yorba Linda', 'Brea', 'Placentia'
// ];

const zipCodes = [
  '90210', '90211', '92626', '92627', '92660', '92663', '92832', '92835', '92780', '92782'
];

// Generate mock leads
export const generateMockLeads = (): Lead[] => {
  const leads: Lead[] = [];
  
  for (let i = 0; i < 55; i++) {
    const firstName = randomChoice(firstNames);
    const lastName = randomChoice(lastNames);
    const channel: Channel = randomChoice(['RETAIL', 'SL']);
    const stage: PipelineStage = randomChoice(['New', 'Engaged', 'Stalled', 'Dropped', 'Converted']);
    const sequenceType: SequenceType = randomChoice(['NEW_LEAD', 'STALLED_APP', 'DROPPED_LEAD', 'PAST_CLIENT_REFI']);
    
    const lead: Lead = {
      id: `lead_${i + 1}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@email.com`,
      phone: `(${randomBetween(200, 999)}) ${randomBetween(200, 999)}-${randomBetween(1000, 9999)}`,
      zipCode: randomChoice(zipCodes),
      channel,
      stage,
      engagementScore: randomBetween(0, 100),
      lastTouch: `${randomBetween(1, 30)} days ago`,
      nextAction: randomChoice([
        'Rate comparison email',
        'SMS follow-up',
        'Call outreach',
        'Application reminder',
        'Document request'
      ]),
      nextActionTime: randomChoice([
        'Tomorrow 9:00 AM',
        'Today 3:00 PM',
        'Monday 10:00 AM',
        'In 2 hours',
        'Friday 2:00 PM'
      ]),
      loanIntent: {
        purpose: randomChoice(['purchase', 'refinance', 'cash_out']),
        propertyValue: randomBetween(300000, 800000),
        loanAmount: randomBetween(200000, 600000),
      },
      source: randomChoice(['Website', 'Referral', 'Social Media', 'Direct Mail', 'Partner']),
      createdAt: new Date(Date.now() - randomBetween(0, 30) * 24 * 60 * 60 * 1000).toISOString(),
      sequenceId: `seq_${randomChoice(['nl001', 'sa001', 'dl001', 'pc001'])}`,
      sequenceType,
    };
    
    leads.push(lead);
  }
  
  return leads;
};

export const mockLeads = generateMockLeads();

export const mockNurtureSequences: NurtureSequence[] = [
  {
    id: 'seq_nl001',
    name: 'New Lead (NL-001)',
    type: 'NEW_LEAD',
    description: 'Welcome sequence for newly ingested leads',
    isActive: true,
    totalTouchpoints: 7,
    avgDuration: 14,
    channels: ['email', 'sms'],
    performance: {
      activeLeads: 847,
      avgEngagementScore: 67.2,
      conversionRate: 28.4,
      completionRate: 73.1,
    },
    touchpoints: [
      {
        id: 'tp_1',
        sequenceId: 'seq_nl001',
        order: 1,
        type: 'email',
        delay: 2,
        name: 'Welcome Email',
        subject: 'Welcome to AFN - Your Rate Quote Inside',
        contentPreview: 'Thanks for your interest in AFN! Here\'s your personalized rate quote...',
        template: 'new-lead-welcome.html',
        personalization: ['first_name', 'rate_quote', 'loan_amount'],
        abTestVariant: {
          name: 'Urgency Subject Line Test',
          isWinner: false,
          metrics: { sent: 1240, opened: 423, clicked: 89, converted: 23 }
        }
      },
      {
        id: 'tp_2',
        sequenceId: 'seq_nl001',
        order: 2,
        type: 'sms',
        delay: 5,
        name: 'SMS Follow-up',
        contentPreview: 'Thanks for your interest. Check your email for next steps.',
        template: 'new-lead-sms.txt',
        personalization: ['first_name'],
      }
    ]
  },
  {
    id: 'seq_sa001',
    name: 'Stalled App (SA-001)',
    type: 'STALLED_APP',
    description: 'Recovery sequence for stalled applications',
    isActive: true,
    totalTouchpoints: 5,
    avgDuration: 30,
    channels: ['email', 'sms', 'voice'],
    performance: {
      activeLeads: 156,
      avgEngagementScore: 42.1,
      conversionRate: 15.7,
      completionRate: 61.8,
    },
    touchpoints: [
      {
        id: 'tp_3',
        sequenceId: 'seq_sa001',
        order: 1,
        type: 'email',
        delay: 4320, // 72 hours
        name: 'Resume Application',
        subject: 'Left off at {{last_step}}. Continue in 30 seconds',
        contentPreview: 'Your application is 75% complete. Finish now...',
        template: 'stall-resume.html',
        personalization: ['first_name', 'last_step', 'completion_percentage'],
      }
    ]
  },
  {
    id: 'seq_dl001',
    name: 'Re-engagement (DL-001)',
    type: 'DROPPED_LEAD',
    description: 'Long-term re-engagement for dropped leads',
    isActive: true,
    totalTouchpoints: 4,
    avgDuration: 180,
    channels: ['email'],
    performance: {
      activeLeads: 94,
      avgEngagementScore: 31.8,
      conversionRate: 8.2,
      completionRate: 45.3,
    },
    touchpoints: [
      {
        id: 'tp_4',
        sequenceId: 'seq_dl001',
        order: 1,
        type: 'email',
        delay: 43200, // 30 days
        name: 'Market Update',
        subject: 'Market update for your area',
        contentPreview: 'Local real estate trends and rate environment updates...',
        template: 'market-update.html',
        personalization: ['first_name', 'zip_code', 'local_market_data'],
      }
    ]
  },
  {
    id: 'seq_pc001',
    name: 'Past Client Refi (PC-001)',
    type: 'PAST_CLIENT_REFI',
    description: 'Refinance opportunities for past clients',
    isActive: true,
    totalTouchpoints: 3,
    avgDuration: 0, // Ongoing/event-driven
    channels: ['email', 'sms'],
    performance: {
      activeLeads: 423,
      avgEngagementScore: 58.9,
      conversionRate: 12.1,
      completionRate: 78.4,
    },
    touchpoints: [
      {
        id: 'tp_5',
        sequenceId: 'seq_pc001',
        order: 1,
        type: 'email',
        delay: 0, // Immediate when opportunity detected
        name: 'Refi Opportunity Alert',
        subject: 'You could save ${{monthly_savings}}/month - worth a look?',
        contentPreview: 'Rates have dropped and you could save significantly...',
        template: 'refi-opportunity.html',
        personalization: ['first_name', 'monthly_savings', 'current_rate', 'new_rate'],
      }
    ]
  }
];

export const mockActivityEvents: ActivityEvent[] = [
  {
    id: 'activity_1',
    timestamp: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    type: 'engagement',
    leadId: 'lead_1',
    leadName: 'John Smith',
    description: 'John Smith opened "Market Update" email',
  },
  {
    id: 'activity_2',
    timestamp: new Date(Date.now() - 4 * 60 * 1000).toISOString(),
    type: 'engagement',
    leadId: 'lead_2',
    leadName: 'Sarah Johnson',
    description: 'Sarah Johnson clicked refi calculator link',
  },
  {
    id: 'activity_3',
    timestamp: new Date(Date.now() - 7 * 60 * 1000).toISOString(),
    type: 'engagement',
    leadId: 'lead_3',
    leadName: 'Mike Davis',
    description: 'Mike Davis replied to SMS - flagged for handoff',
  },
  {
    id: 'activity_4',
    timestamp: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    type: 'engagement',
    leadId: 'lead_4',
    leadName: 'Amanda Wilson',
    description: 'Application resumed: Amanda Wilson',
  },
  {
    id: 'activity_5',
    timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    type: 'stall_detected',
    leadId: 'lead_5',
    leadName: 'Robert Chen',
    description: 'Stall detected: Robert Chen (72hr timeout)',
  },
];

export const mockEngagementMetrics: EngagementMetrics = {
  timeframe: '7d',
  email: {
    sent: 2847,
    delivered: 2762,
    opened: 679,
    clicked: 108,
    replied: 23,
    bounced: 85,
    unsubscribed: 8,
  },
  sms: {
    sent: 1456,
    delivered: 1372,
    replied: 112,
    optedOut: 16,
    clicked: 170,
  },
  voice: {
    attempted: 234,
    answered: 102,
    avgDuration: 222, // seconds
    handoffRate: 67,
    callbackRequests: 23,
  },
  chat: {
    sessions: 445,
    avgDuration: 255, // seconds
    questionsAnswered: 1023,
    resolutionRate: 78,
  },
};

export const mockConversionFunnel: ConversionFunnel = {
  period: 'This Week',
  stages: [
    {
      name: 'New Leads',
      count: 854,
      conversionRate: 100,
    },
    {
      name: 'First Touch',
      count: 801,
      conversionRate: 93.8,
      dropoffReasons: ['53 leads lost at first touch (deliverability)'],
    },
    {
      name: 'Engaged',
      count: 398,
      conversionRate: 49.7,
      dropoffReasons: ['403 leads didn\'t engage (content/timing)'],
    },
    {
      name: 'Application',
      count: 127,
      conversionRate: 31.9,
      dropoffReasons: ['271 engaged but didn\'t apply (qualification/interest)'],
    },
    {
      name: 'Handoff',
      count: 89,
      conversionRate: 70.1,
      dropoffReasons: ['38 applied but not ready for handoff (stalled)'],
    },
  ],
};

export const mockPastClients: PastClient[] = [
  {
    id: 'client_1',
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah.johnson@email.com',
    phone: '(555) 123-4567',
    channel: 'RETAIL',
    originalLoan: {
      date: '2023-10-15',
      amount: 450000,
      rate: 4.75,
      term: 30,
      monthlyPayment: 2347.58,
    },
    refiOpportunity: {
      newRate: 3.25,
      monthlySavings: 487,
      totalSavings: 175320,
      breakEvenMonths: 18,
      urgency: 'high',
      contactStatus: 'contacted',
      lastContact: '2026-04-11',
    },
    engagementHistory: [
      {
        date: '2026-04-11',
        type: 'email',
        subject: 'Rate drop alert sent',
        response: 'Email opened',
        sentiment: 'positive',
      },
    ],
  },
  {
    id: 'client_2',
    firstName: 'Mike',
    lastName: 'Chen',
    email: 'mike.chen@email.com',
    phone: '(555) 234-5678',
    channel: 'SL',
    originalLoan: {
      date: '2023-08-22',
      amount: 380000,
      rate: 5.125,
      term: 30,
      monthlyPayment: 2067.45,
    },
    refiOpportunity: {
      newRate: 3.25,
      monthlySavings: 623,
      totalSavings: 224280,
      breakEvenMonths: 16,
      urgency: 'high',
      contactStatus: 'not_contacted',
    },
    engagementHistory: [],
  },
];

export const mockDashboardMetrics: DashboardMetrics = {
  activeLeads: {
    total: 2847,
    todayChange: 12,
    weeklyNew: 342,
    weeklyStalled: 156,
  },
  handoffs: {
    ready: 89,
    pending: 45,
    completed: 234,
  },
  responseRate: {
    current: 24.6,
    weekOverWeekChange: 2.1,
  },
  systemHealth: {
    uptime: 99.8,
    status: 'healthy',
    issues: [],
  },
};